import React, { useState } from 'react';
import styles from './Purchases.module.css';
import purchaseData from '../../data/purchaseData.json';

// Import Icons, Tooltip, IconButton, and Pagination
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Tooltip, IconButton, Pagination } from '@mui/material';

// Define the structure of a purchase order
interface Purchase {
    id: number;
    poNumber: string;
    supplierCode: string;
    supplierName: string;
    orderDate: string;
    deliveryDate: string;
    status: string;
    items: {
        productCode: string;
        productName: string;
        quantity: number;
        unitPrice: number;
        total: number;
    }[];
    totalAmount: number;
    paymentStatus: string;
    paymentMethod: string;
    notes: string;
}

const Purchases: React.FC = () => {
    const [purchases, setPurchases] = useState<Purchase[]>(purchaseData);
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Filter states
    const [poNumberFilter, setPoNumberFilter] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Calculate pagination indices
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = purchases.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(purchases.length / itemsPerPage);

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    // Placeholder handlers
    const handleSearch = () => {
        console.log('Searching Purchases...', { 
            poNumberFilter, 
            customerFilter, 
            statusFilter, 
            dateFilter, 
            paymentStatusFilter,
            searchTerm 
        });
        // Add actual filtering logic here
    };

    const handleReset = () => {
        setPoNumberFilter('');
        setCustomerFilter('');
        setStatusFilter('');
        setDateFilter('');
        setPaymentStatusFilter('');
        setSearchTerm('');
        setCurrentPage(1); // Reset to first page
        console.log('Purchase Filters reset');
    };

    const handleAddPurchase = () => {
        console.log('Add new purchase order');
        // Logic to open a modal or navigate to add form
    };

    const handleViewDetails = (id: number) => {
        console.log(`View details for PO ID: ${id}`);
    };
    
    const handleEditPurchase = (id: number) => {
        console.log(`Edit PO ID: ${id}`);
    };

    const handleGenerateInvoice = (poNumber: string) => {
        console.log(`Generate invoice for PO: ${poNumber}`);
    };

    // Function to get status badge class
    const getStatusClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return styles.success;
            case 'cancelled':
                return styles.danger;
            case 'pending':
                return styles.warning;
            case 'processing':
                return styles.info;
            default:
                return styles.default;
        }
    };

    // Function to get payment status badge class
    const getPaymentStatusClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'paid':
                return styles.success;
            case 'unpaid':
                return styles.danger;
            case 'partial':
                return styles.warning;
            default:
                return styles.default;
        }
    };

    // Function to format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className={styles.container}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h2>Purchase Orders</h2>
                    <p>Manage your purchase orders</p>
                </div>
                <div className={styles.actionsSection}>
                    <button className={styles.actionButton}>
                        <FileDownloadIcon fontSize="small"/> Export Orders
                    </button>
                    <button 
                        className={`${styles.actionButton} ${styles.addButton}`}
                        onClick={handleAddPurchase}
                    >
                        <AddShoppingCartIcon fontSize="small"/> Create Order
                    </button>
                </div>
            </div>

            {/* Filter Area */}
            <div className={styles.filterArea}>
                <div className={styles.filterGroup}>
                    <label>PO Number</label>
                    <input 
                        type="text" 
                        placeholder="Search by PO number"
                        value={poNumberFilter}
                        onChange={(e) => setPoNumberFilter(e.target.value)}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Customer</label>
                    <input 
                        type="text" 
                        placeholder="Search by customer"
                        value={customerFilter}
                        onChange={(e) => setCustomerFilter(e.target.value)}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Status</label>
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All</option>
                        <option value="Pending">Pending</option>
                        <option value="Processing">Processing</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Order Date</label>
                    <input 
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Payment Status</label>
                    <select 
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                    >
                        <option value="">All</option>
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid</option>
                        <option value="Partial">Partial</option>
                    </select>
                </div>
                <div className={styles.filterActions}>
                    <button 
                        className={`${styles.actionButton} ${styles.searchButton}`}
                        onClick={handleSearch}
                    >
                        <SearchIcon fontSize="small"/> Search
                    </button>
                    <button 
                        className={`${styles.actionButton} ${styles.resetButton}`}
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* Table Container */}
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th>PO Number</th>
                            <th>Customer</th>
                            <th>Order Date</th>
                            <th>Delivery Date</th>
                            <th>Status</th>
                            <th>Payment Status</th>
                            <th>Total Items</th>
                            <th>Total Amount</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((purchase) => (
                            <tr key={purchase.id}>
                                <td className={styles.poNumber}>
                                    {purchase.poNumber}
                                </td>
                                <td>{purchase.supplierName}</td>
                                <td>{purchase.orderDate}</td>
                                <td>{purchase.deliveryDate}</td>
                                <td>
                                    <span className={`${styles.statusBadge} ${getStatusClass(purchase.status)}`}>
                                        {purchase.status}
                                    </span>
                                </td>
                                <td>
                                    <span className={`${styles.statusBadge} ${getPaymentStatusClass(purchase.paymentStatus)}`}>
                                        {purchase.paymentStatus}
                                    </span>
                                </td>
                                <td>{purchase.items.length}</td>
                                <td>{formatCurrency(purchase.totalAmount)}</td>
                                <td className={styles.actionIcons}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewDetails(purchase.id)}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Order">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditPurchase(purchase.id)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Generate Invoice">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleGenerateInvoice(purchase.poNumber)}
                                        >
                                            <ReceiptIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className={styles.paginationContainer}>
                <Pagination 
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                />
            </div>
        </div>
    );
};

export default Purchases; 